import {DialogPrimitive as Dialog} from "ch3-ui-lib";
import {PhoneHQLoader, PhoneHQButton, PhoneHQInput, PhoneHQRadioCards} from 'ch3-ui-lib';
import {cloneDeep, omit, set} from "lodash";
import {Plus, Trash2, X} from "lucide-react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {
    type Action,
    useCreateQueueMutation,
    useDeleteQueueMutation,
    useGetCallQueueRecordingQuery,
    useGetQueueByIdQuery,
    useGetQueuePhoneNumbersQuery,
    useGetQueuesQuery,
    useLazyGetPlayMessageQuery,
    usePostCallQueueRecordingPreviewMutation,
    useUpdateQueueMutation,
    useUploadCallQueueSynthetizeTextMutation,
} from "../../store/features/CallQueue/CallQueueService";
import {useSynthetizeTextMutation} from "../../store/features/MsisdnSettings/msisdnSettingsService";
import {selectUserId} from "../../store/features/Auth/authSlice";
import {showToast} from "../../store/features/Notifications/notificationsSlice";
import {useAppDispatch} from "../../store/store";
import {getFormatedPhoneNumber} from "../../utilis/msisdnUtilis";
import {destinationRefusalOf, type DestinationRefusal, forwardingRefusalMessage, refusalOutcome} from "../../utils/callRestrictions/apiErrors";
import {MessageType} from "../../utilis/types/Types";
import {useAudioFiles, useMsisdnAndUsers} from "./hooks/CallQueueHooks";
import type {DestinationChoice, DestinationPick} from "./DestinationMenu";
import {ExternalNumberDialog} from "./ExternalNumberDialog";
import {ConfirmDialog} from "../../components/ConfirmDialog/ConfirmDialog";
import {QueueKeyCard} from "./QueueKeyCard";
import {MessageDialog, type TtsSettings} from "../../components/MessageDialog/MessageDialog";
import {
    availableKeys,
    destinationLabel,
    emptyQueue,
    externalTargets,
    hasErrors,
    MENU_REPEATS_MAX,
    nextFreeKey,
    type QueueErrors,
    type QueueFormValues,
    refusedOptionIndexes,
    STRATEGIES,
    toQueuePayload,
    validateQueue,
} from "./queueModel";
import {AudioCard, lucide, QueueCard, QueueSection, SmallButton, Toggle, ToggleRow} from "./queueUi";

type MessageTarget = MessageType.WELCOME_MESSAGE | MessageType.MENU_MESSAGE | `PLAY_MESSAGE_${number}`;

/** Labels are UI-only (old forms stored JSX there); dirty checks ignore them. */
const snapshot = (v: QueueFormValues) =>
    JSON.stringify({...v, ivrMenuOption: {...v.ivrMenuOption, actions: v.ivrMenuOption.actions.map((a) => omit(a, 'label'))}});

interface QueueDrawerProps {
    /** Queue id, or "new". */
    queueId: string;
    onClose: () => void;
}

/** Queues-Create / Queues-Edit: right-hand drawer over the list. */
export const QueueDrawer = ({queueId, onClose}: QueueDrawerProps) => {
    const dispatch = useAppDispatch();
    const userId = useSelector(selectUserId);
    const isNew = queueId === 'new';
    const id = isNew ? undefined : Number(queueId);

    const {data: queue, isLoading: queueLoading} = useGetQueueByIdQuery(id!, {skip: isNew});
    const {data: queueNumbers} = useGetQueuePhoneNumbersQuery(id!, {skip: isNew});
    const {data: queueList} = useGetQueuesQuery();
    const {data: greetingsAudioUrl} = useGetCallQueueRecordingQuery({id: queueId, type: MessageType.WELCOME_MESSAGE}, {skip: isNew});
    const {data: menuAudioUrl} = useGetCallQueueRecordingQuery({id: queueId, type: MessageType.MENU_MESSAGE}, {skip: isNew});
    const [getPlayMessage] = useLazyGetPlayMessageQuery();
    const [createQueue, {isLoading: creating}] = useCreateQueueMutation();
    const [updateQueue, {isLoading: updating}] = useUpdateQueueMutation();
    const [deleteQueue, {isLoading: deleting}] = useDeleteQueueMutation();

    const [postRecordingPreview, {data: tempFile, reset: resetTempFile}] = usePostCallQueueRecordingPreviewMutation();
    const [synthetizeText, {data: tempTtsFile}] = useSynthetizeTextMutation();
    const [postSynthTextPreview, {data: synthText, reset: resetSynthText}] = useUploadCallQueueSynthetizeTextMutation();

    const {phoneNumbersItems, userItems, groupItems, isLoading: listsLoading, onGroupEnd} = useMsisdnAndUsers({userId});

    const [values, setValues] = useState<QueueFormValues>(emptyQueue);
    const initial = useRef(snapshot(emptyQueue()));
    const [loaded, setLoaded] = useState(isNew);
    const [errors, setErrors] = useState<QueueErrors>({});
    const [submitted, setSubmitted] = useState(false);
    const [confirmLeave, setConfirmLeave] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [messageTarget, setMessageTarget] = useState<MessageTarget | null>(null);
    const [pendingUpload, setPendingUpload] = useState<string>('');
    const [savedTts, setSavedTts] = useState<TtsSettings | undefined>();
    const [externalFor, setExternalFor] = useState<number | null>(null);
    const [forwardRefusal, setForwardRefusal] = useState<{refusal: DestinationRefusal; numbers: string[]}>();

    // react-hook-form style setter, kept for the shared audio hook.
    const setValue = useCallback((path: string, value: unknown) => {
        setValues((prev) => {
            const next = cloneDeep(prev);
            set(next, path, value);
            return next;
        });
    }, []);

    const {audioFiles, setAudioFiles, updateAudioFiles, removeAudioFile, setSavedTextToSpeech} =
        useAudioFiles({greetingsAudioUrl, menuAudioUrl, setValue});

    // Load an existing queue once.
    useEffect(() => {
        if (isNew || !queue || loaded) return;
        const base = emptyQueue();
        const ivr = queue.ivrMenuOption;
        const next: QueueFormValues = {
            name: queue.name ?? '',
            ringStrategy: queue.ringStrategy ?? 'RING_ALL',
            ivrMenuOption: {
                enabled: Boolean(ivr?.enabled),
                maxMenuRepeats: ivr?.maxMenuRepeats || 3,
                actions: ivr?.actions?.length ? ivr.actions.map((a) => ({...a, order: String(a.order)})) : base.ivrMenuOption.actions,
                messageSettings: {
                    WELCOME_MESSAGE: {
                        originalFileName: ivr?.messageSettings?.WELCOME_MESSAGE?.originalFileName ?? '',
                        bucketFileName: ivr?.messageSettings?.WELCOME_MESSAGE?.bucketFileName ?? '',
                    },
                    MENU_MESSAGE: {
                        originalFileName: ivr?.messageSettings?.MENU_MESSAGE?.originalFileName ?? '',
                        bucketFileName: ivr?.messageSettings?.MENU_MESSAGE?.bucketFileName ?? '',
                    },
                },
            },
        };
        setValues(next);
        initial.current = snapshot(next);
        setLoaded(true);

        // Fetch the audio for "Play message" keys.
        next.ivrMenuOption.actions.forEach(async (a, index) => {
            if (a.type !== 'PlayMessage') return;
            const {data} = await getPlayMessage({id: queueId, order: Number(a.order)});
            setAudioFiles((prev: { PLAY_MESSAGE?: Record<number, string> }) => ({...prev, PLAY_MESSAGE: {...prev.PLAY_MESSAGE, [index]: data?.data}}));
        });
    }, [queue, isNew, loaded, queueId, getPlayMessage, setAudioFiles]);

    // A finished recording / upload / speech lands in tempFile or synthText; attach it to the target message.
    useEffect(() => {
        if ((!tempFile && !synthText) || !pendingUpload) return;
        if (tempFile) {
            updateAudioFiles(audioFiles, tempFile, pendingUpload);
            resetTempFile();
        }
        if (synthText) {
            setSavedTextToSpeech(savedTts, pendingUpload);
            updateAudioFiles(audioFiles, {...synthText, ...tempTtsFile}, pendingUpload);
            resetSynthText();
        }
        setPendingUpload('');
    }, [tempFile, synthText, pendingUpload]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (submitted) setErrors(validateQueue(values, queueList ?? [], queueId));
    }, [values, submitted, queueList, queueId]);

    const dirty = loaded && snapshot(values) !== initial.current;
    const ivr = values.ivrMenuOption;
    const actions = ivr.actions;

    const toChoices = (items: { value: string; label: string; prefixElement?: React.ReactNode }[]): DestinationChoice[] =>
        items.map((i) => ({value: i.value, label: i.label, prefix: i.prefixElement}));
    const users = useMemo(() => toChoices(userItems), [userItems]);
    const groups = useMemo(() => toChoices(groupItems), [groupItems]);
    const numbers = useMemo(() => toChoices(phoneNumbersItems), [phoneNumbersItems]);

    const refusedNow = forwardRefusal ? refusedOptionIndexes(values, forwardRefusal.numbers) : [];
    const keyError = (index: number) =>
        errors.keys?.[index] ?? (forwardRefusal && refusedNow.includes(index) ? forwardingRefusalMessage(forwardRefusal.refusal) : undefined);

    const requestClose = () => (dirty ? setConfirmLeave(true) : onClose());

    const patchAction = (index: number, patch: Partial<Action>) =>
        setValues((prev) => {
            const next = cloneDeep(prev);
            next.ivrMenuOption.actions[index] = {...next.ivrMenuOption.actions[index], ...patch};
            return next;
        });

    const pickDestination = (index: number, pick: DestinationPick) => {
        const cleared = {userId: undefined, groupId: undefined, phoneNumberId: undefined, phoneNumber: undefined, messageSettings: undefined};
        switch (pick.type) {
            case 'ForwardToExternalNumber':
                setExternalFor(index);
                return;
            case 'PlayMessage':
                setMessageTarget(`PLAY_MESSAGE_${index}`);
                return;
            default:
                patchAction(index, {...cleared, ...pick});
        }
    };

    const addOption = () => {
        const key = nextFreeKey(actions);
        if (!key) return;
        setValue('ivrMenuOption.actions', [...actions, {type: '', order: key, keyName: ''}]);
    };

    const removeOption = (index: number) => setValue('ivrMenuOption.actions', actions.filter((_, i) => i !== index));

    const toggleGreeting = () => {
        if (ivr.messageSettings.WELCOME_MESSAGE.originalFileName) {
            setValue('ivrMenuOption.messageSettings.WELCOME_MESSAGE', {originalFileName: '', bucketFileName: ''});
            removeAudioFile(MessageType.WELCOME_MESSAGE);
        } else {
            setMessageTarget(MessageType.WELCOME_MESSAGE);
        }
    };

    const save = async () => {
        setSubmitted(true);
        const found = validateQueue(values, queueList ?? [], queueId);
        setErrors(found);
        if (hasErrors(found)) {
            if (found.name) document.getElementById('queue-name')?.focus();
            return;
        }
        try {
            const data = toQueuePayload(values);
            if (isNew) await createQueue(data).unwrap();
            else await updateQueue({id: id!, data}).unwrap();
            dispatch(showToast({description: 'Call queue saved.', type: 'success'}));
            onClose();
        } catch (error) {
            const refusal = destinationRefusalOf(error);
            if (refusal) {
                const outcome = refusalOutcome(refusal, externalTargets(actions, ivr.enabled));
                if (outcome.refused) setForwardRefusal({refusal, numbers: Object.values<string | undefined>(outcome.refused).flatMap((n) => (n ? [n] : []))});
                else dispatch(showToast({description: outcome.toast, type: 'error'}));
                return;
            }
            dispatch(showToast({description: 'Couldn’t save the call queue. Try again.', type: 'error'}));
        }
    };

    const remove = async () => {
        try {
            await deleteQueue(id!).unwrap();
            dispatch(showToast({description: 'Call queue deleted.', type: 'success'}));
            onClose();
        } catch {
            dispatch(showToast({description: 'Couldn’t delete the call queue. Try again.', type: 'error'}));
        }
    };

    const messageTitle = messageTarget === MessageType.WELCOME_MESSAGE
        ? 'Greeting message'
        : messageTarget === MessageType.MENU_MESSAGE
            ? 'Menu message'
            : messageTarget ? `Message for key ${actions[Number(messageTarget.split('_')[2])]?.order}` : '';
    const messageAudio = messageTarget?.startsWith('PLAY_MESSAGE_')
        ? audioFiles?.PLAY_MESSAGE?.[messageTarget.split('_')[2]]
        : messageTarget ? audioFiles?.[messageTarget] : undefined;

    const busy = creating || updating;
    const loading = (!isNew && (queueLoading || !loaded)) || listsLoading;
    const title = isNew ? 'Create call queue' : (queue?.name || 'Call queue');

    return (
        <Dialog.Root open onOpenChange={(open) => !open && requestClose()}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim data-[state=open]:animate-in data-[state=open]:fade-in-0"/>
                <Dialog.Content
                    aria-describedby={undefined}
                    className="fixed bottom-0 right-0 top-0 z-40 flex w-[min(640px,100%)] flex-col bg-surface text-ink shadow-overlay outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-right"
                >
                    <div className="flex h-14 shrink-0 items-center gap-3 border-0 border-b border-solid border-line px-5">
                        <div className="flex min-w-0 flex-1 flex-col">
                            <Dialog.Title className="m-0 truncate text-body-strong leading-tight">{title}</Dialog.Title>
                            {!isNew && <span className="text-footnote text-ink-muted">Call queue</span>}
                        </div>
                        <button type="button" aria-label="Close" onClick={requestClose}
                                className="flex size-8 cursor-pointer items-center justify-center rounded-chip text-ink hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus">
                            <X size={18} aria-hidden="true" className={lucide}/>
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex flex-1 items-center justify-center"><PhoneHQLoader/></div>
                    ) : (
                        <div className="min-h-0 flex-1 overflow-y-auto p-5">
                            <div className="flex flex-col gap-3.5">
                                <QueueSection title="Queue name" description="Enter a name to identify this call queue (e.g., Support Line, Sales Queue).">
                                    <PhoneHQInput
                                        id="queue-name"
                                        label="Name"
                                        placeholder="Queue name"
                                        value={values.name}
                                        error={errors.name}
                                        onChange={(e) => setValue('name', e.target.value)}
                                    />
                                </QueueSection>

                                <QueueSection title="Call routing strategy" description="Select how calls are routed to agents in this queue.">
                                    <PhoneHQRadioCards label="Call routing strategy" value={values.ringStrategy} onChange={(v) => setValue('ringStrategy', v)} options={STRATEGIES}/>
                                </QueueSection>

                                <QueueSection title="Phone tree" description="Customize your phone tree menu.">
                                    <QueueCard>
                                        <ToggleRow title="Activate automated menu" description="Enable to set up a phone menu for this queue."
                                                   checked={ivr.enabled} onChange={() => setValue('ivrMenuOption.enabled', !ivr.enabled)}/>
                                    </QueueCard>
                                </QueueSection>

                                {ivr.enabled && (
                                    <>
                                        <QueueCard>
                                            <div className="flex flex-col gap-2.5 px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex-1">
                                                        <div className="text-callout text-ink">Greeting message</div>
                                                        <div className="text-footnote text-ink-muted">Create a greeting message.</div>
                                                    </div>
                                                    <SmallButton onClick={() => setMessageTarget(MessageType.WELCOME_MESSAGE)}>
                                                        {ivr.messageSettings.WELCOME_MESSAGE.originalFileName ? 'Change' : 'Set up'}
                                                    </SmallButton>
                                                    <Toggle label="Greeting message" checked={Boolean(ivr.messageSettings.WELCOME_MESSAGE.originalFileName)} onChange={toggleGreeting}/>
                                                </div>
                                                {audioFiles?.WELCOME_MESSAGE && ivr.messageSettings.WELCOME_MESSAGE.originalFileName && (
                                                    <AudioCard title="Greeting message" src={audioFiles.WELCOME_MESSAGE}/>
                                                )}
                                            </div>
                                        </QueueCard>

                                        <QueueSection title="Numeric options" description="Menu message played to callers. For example: For customer service, press 1.">
                                            <div className="flex flex-col gap-2.5">
                                                <div className="flex items-center gap-3">
                                                    <span className={`flex-1 text-callout ${errors.menuMessage ? 'text-danger-ink' : 'text-ink'}`}>Menu message</span>
                                                    <SmallButton onClick={() => setMessageTarget(MessageType.MENU_MESSAGE)}>
                                                        {ivr.messageSettings.MENU_MESSAGE.originalFileName ? 'Change' : 'Set up'}
                                                    </SmallButton>
                                                </div>
                                                {errors.menuMessage && <p role="alert" className="m-0 text-footnote text-danger-ink">{errors.menuMessage}</p>}
                                                {audioFiles?.MENU_MESSAGE && ivr.messageSettings.MENU_MESSAGE.originalFileName && (
                                                    <AudioCard title="Menu message" src={audioFiles.MENU_MESSAGE}/>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-3 px-1">
                                                <div className="flex-1">
                                                    <label htmlFor="queue-repeats" className="text-[15px] font-semibold text-ink">Repeat menu and redirect</label>
                                                    <div className="text-footnote text-ink-muted">After the set repetitions, redirect to key 0.</div>
                                                </div>
                                                <span className="box-border flex h-10 w-24 items-center gap-1 rounded-control border border-solid border-fill-subtle bg-surface px-3 focus-within:ring-2 focus-within:ring-brand-focus">
                                                    <input
                                                        id="queue-repeats"
                                                        type="number"
                                                        min={1}
                                                        max={MENU_REPEATS_MAX}
                                                        value={ivr.maxMenuRepeats}
                                                        onChange={(e) => setValue('ivrMenuOption.maxMenuRepeats', Math.min(MENU_REPEATS_MAX, Math.max(1, Number(e.target.value) || 1)))}
                                                        className="w-6 min-w-0 flex-1 border-0 bg-transparent p-0 text-[15px] text-ink outline-none"
                                                    />
                                                    <span className="text-footnote text-ink-muted">times</span>
                                                </span>
                                            </div>

                                            {actions.map((action, index) => (
                                                <QueueKeyCard
                                                    key={`${index}-${action.order}`}
                                                    index={index}
                                                    action={action}
                                                    freeKeys={availableKeys(actions, String(action.order))}
                                                    destination={destinationLabel(action, {users, groups, numbers}, (n) => getFormatedPhoneNumber(n, true))}
                                                    error={keyError(index)}
                                                    ringStrategy={values.ringStrategy}
                                                    users={users}
                                                    groups={groups}
                                                    numbers={numbers}
                                                    onGroupsEnd={onGroupEnd}
                                                    playMessageSrc={audioFiles?.PLAY_MESSAGE?.[index]}
                                                    onChange={(patch) => patchAction(index, patch)}
                                                    onPick={(pick) => pickDestination(index, pick)}
                                                    onEditMessage={() => setMessageTarget(`PLAY_MESSAGE_${index}`)}
                                                    onRemove={() => removeOption(index)}
                                                />
                                            ))}

                                            {nextFreeKey(actions) && (
                                                <button type="button" onClick={addOption}
                                                        className="flex h-9 w-fit cursor-pointer items-center gap-2 rounded-control border-0 bg-transparent px-1 text-[15px] font-semibold text-brand hover:text-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus">
                                                    <Plus size={18} aria-hidden="true" className={lucide}/>
                                                    Add another option
                                                </button>
                                            )}
                                        </QueueSection>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="flex h-16 shrink-0 items-center gap-3 border-0 border-t border-solid border-line bg-surface px-5">
                        {!isNew && (
                            <button type="button" onClick={() => setConfirmDelete(true)}
                                    className="flex h-9 cursor-pointer items-center gap-2 rounded-control border-0 bg-transparent px-2 text-callout font-semibold text-danger-ink hover:bg-danger-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus">
                                <Trash2 size={16} aria-hidden="true" className={lucide}/>
                                Delete queue
                            </button>
                        )}
                        <span className="flex-1"/>
                        <PhoneHQButton variant="secondary" className="px-5" onClick={requestClose}>Cancel</PhoneHQButton>
                        <PhoneHQButton className="px-5" disabled={busy || loading || refusedNow.length > 0} onClick={save}>{isNew ? 'Create' : 'Save changes'}</PhoneHQButton>
                    </div>

                    <MessageDialog
                        open={messageTarget !== null}
                        onOpenChange={(open) => !open && setMessageTarget(null)}
                        title={messageTitle}
                        textLabel={messageTitle}
                        onSaved={() => messageTarget && setPendingUpload(messageTarget)}
                        audioFile={messageAudio}
                        savedTts={savedTts}
                        setSavedTts={setSavedTts}
                        uploadFile={postRecordingPreview}
                        synthetizeText={synthetizeText}
                        uploadSynthText={postSynthTextPreview}
                    />
                    <ExternalNumberDialog
                        open={externalFor !== null}
                        onOpenChange={(open) => !open && setExternalFor(null)}
                        initial={externalFor !== null ? actions[externalFor]?.phoneNumber : undefined}
                        onSave={(phoneNumber) => externalFor !== null && patchAction(externalFor, {
                            type: 'ForwardToExternalNumber', phoneNumber,
                            userId: undefined, groupId: undefined, phoneNumberId: undefined, messageSettings: undefined,
                        })}
                    />
                    <ConfirmDialog
                        open={confirmLeave}
                        onOpenChange={setConfirmLeave}
                        title="Unsaved changes"
                        cancelLabel="Keep editing"
                        confirmLabel="Leave"
                        tone="leave"
                        onConfirm={() => {
                            setConfirmLeave(false);
                            onClose();
                        }}
                    >
                        You have unsaved changes. Leave without saving?
                    </ConfirmDialog>
                    <ConfirmDialog
                        open={confirmDelete}
                        onOpenChange={setConfirmDelete}
                        title={`Delete “${values.name || 'this queue'}”?`}
                        cancelLabel="Cancel"
                        confirmLabel="Delete queue"
                        tone="danger"
                        busy={deleting}
                        onConfirm={remove}
                    >
                        {queueNumbers?.data?.length
                            ? <>These numbers will be disconnected from the queue: {queueNumbers.data.map((n: { label: string }) => n.label).join(', ')}. This can’t be undone.</>
                            : 'This can’t be undone.'}
                    </ConfirmDialog>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
